import {
  ChartDataRequest,
  OrgsChartResponse,
} from '../domain/AdminDashboard/stores/admDashboardStore';
import {
  LockUnlockUsersCommand,
  SetSubscriptionCommand,
  Subscription,
  UpdateSubscriptionFeaturesCommand,
} from '../domain/AdminOrganizations/stores/objects/Organization';
import {
  OrgDetailsDto,
  OrgListItemDto,
  TenantDataDto,
} from '../domain/AdminOrganizations/stores/orgListStore';
import { CompanyDetails } from '../domain/Company/stores/companyStore';
import { GetTeamsQuery, Team } from '../domain/Groups/stores/teamsStore';
import { Guido } from '../domain/Guidos/stores/guidosStore';
import {
  ApplicationUser,
  GetUsersQuery,
  PlainObject,
} from '../domain/Users/stores/usersStore';
import { Result } from '../services/rootStore';
import { BaseQuery, ListResult } from '../types/commonTypes';
import { RestClient } from './restClient';

export type GuidoDetailsResponse = {
  publicLink: string;
};

export class TransportLayer {
  apiClient: RestClient;

  constructor(client: RestClient) {
    this.apiClient = client;
  }

  public async fetchUsers(
    query: Partial<GetUsersQuery>
  ): Promise<Result<ListResult<ApplicationUser>>> {
    return await this.apiClient.post<
      Partial<GetUsersQuery>,
      Result<ListResult<ApplicationUser>>
    >('/user/users', query);
  }

  public async fetchRolesMeta() {
    return await this.apiClient.get<Result<PlainObject>>('/meta/userRoles');
  }

  public async fetchAllSubscriptionTypes() {
    return await this.apiClient.get<Result<{ [key: string]: string }>>(
      '/admin/subscription/all'
    );
  }

  async fetchSubscription(subscriptionId: string) {
    return await this.apiClient.get<Result<Subscription>>(
      `/admin/subscription/subscription/${subscriptionId}`
    );
  }

  async setTenantSubscription(command: SetSubscriptionCommand) {
    return await this.apiClient.post<
      SetSubscriptionCommand,
      Result<Subscription>
    >('/admin/subscription/setTenantSubscription', command);
  }

  async updateSubscriptionFeatures(command: UpdateSubscriptionFeaturesCommand) {
    return await this.apiClient.put<
      UpdateSubscriptionFeaturesCommand,
      Result<any>
    >('/admin/subscription/updateTenantSubscriptionFeatureValues', command);
  }

  async fetchOrgs(query: BaseQuery) {
    return await this.apiClient.post<
      BaseQuery,
      Result<ListResult<OrgListItemDto>>
    >('/admin/tenant/tenants', query);
  }

  async fetchOrgById(orgId: string) {
    return await this.apiClient.get<Result<OrgDetailsDto>>(
      `/admin/tenant/tenantdetails/${orgId}`
    );
  }

  async fetchTenantData(tenantId: string) {
    return await this.apiClient.get<Result<TenantDataDto>>(
      `/admin/tenant/tenantdata/${tenantId}`
    );
  }

  async deleteTenantData(tenantId: string, tenantName: string) {
    return await this.apiClient.deleteWithBody<
      { tenantId: string; tenantName: string },
      Result<string>
    >('/admin/tenant/deletetenantdata', { tenantId, tenantName });
  }

  async generateOrganisationCSV() {
    return await this.apiClient.postDownload<any>(
      `/admin/tenant/ExportTenantsToCsv`,
      {
        fileName: 'companyList.csv',
      }
    );
  }

  async lockUnlockUsers(command: LockUnlockUsersCommand) {
    return await this.apiClient.put<LockUnlockUsersCommand, Result<string>>(
      '/admin/user/lock',
      command
    );
  }

  async fetchOrgUsersChartValues() {
    return await this.apiClient.get<Result<OrgsChartResponse>>(
      '/admin/Dashboard/organization-users/chart'
    );
  }

  async fetchOrgGuidosChartValues() {
    return await this.apiClient.get<Result<OrgsChartResponse>>(
      '/admin/Dashboard/organization-guidos/chart'
    );
  }

  async fetchOrgSubscriptionsChartValues() {
    return await this.apiClient.get<Result<OrgsChartResponse>>(
      '/admin/Dashboard/organization-subscriptions/chart'
    );
  }

  async fetchChartValuesByCategory(request: ChartDataRequest) {
    return await this.apiClient.post<
      ChartDataRequest,
      Result<{ values: { [key: string]: number } }>
    >('/admin/Dashboard/CategoryValuesByPeriod', request);
  }

  async fetchCompanyDetails(companyId: string) {
    return await this.apiClient.get<
      Result<Omit<CompanyDetails, 'logo'> & { logo: string }>
    >(`/company/company/${companyId}`);
  }

  async fetchAllGuidos(query: BaseQuery) {
    return await this.apiClient.post<BaseQuery, Result<ListResult<Guido>>>(
      '/guido/guidos',
      query
    );
  }

  async fetchGuidoDetails(guidoId: string) {
    return await this.apiClient.get<Result<GuidoDetailsResponse>>(
      `/guido/guido/${guidoId}`
    );
  }

  async fetchAllTeams(query: GetTeamsQuery) {
    return await this.apiClient.post<GetTeamsQuery, Result<ListResult<Team>>>(
      '/team/teams',
      query
    );
  }

  async prepareAcademy() {
    return await this.apiClient.post('/academy/prepareacademy', {});
  }

  async fetchTemplates() {
    return await this.apiClient.get<Result<TemplateDto[]>>('/template/GetTemplates');
  }

  async fetchGuidedDoingTemplates() {
    return await this.apiClient.get<Result<TemplateDto[]>>('/template/GetGuidedDoingTemplates');
  }


  async setDefaultTemplate(templateId: string, slot: TemplateDefaultSlot) {
    return await this.apiClient.post<
      { templateId: string; slot: TemplateDefaultSlot },
      Result<string>
    >('/template/SetDefault', { templateId, slot });
  }

  async updateTemplate(templateId: string, title: string, elements: ElementDto[]) {
    return await this.apiClient.put<
      { TemplateId: string; Title: string; Elements: ElementDto[] },
      Result<string>
    >(
      '/template/UpdateTemplate',
      {
        TemplateId: templateId,
        Title: title,
        Elements: elements,
      }
    );
  }

  async createTemplate(sourceTemplateId: string, title: string, elements: ElementDto[]) {
    return await this.apiClient.post<
      { SourceTemplateId: string; Title: string; Elements: ElementDto[] },
      Result<string>
    >(
      '/template/CreateTemplate',
      {
        SourceTemplateId: sourceTemplateId,
        Title: title,
        Elements: elements,
      }
    );
  }

  async assignTemplates(targetTenantId: string, templateIds: string[]) {
    return await this.apiClient.post<
      { TargetTenantId: string; TemplateIds: string[] },
      Result<string>
    >(
      '/template/AssignTemplates',
      {
        TargetTenantId: targetTenantId,
        TemplateIds: templateIds,
      }
    );
  }

  async createCanvasTemplate(title: string, objects: CanvasObjectDto[], previewUrl?: string) {
    return await this.apiClient.post<
      { Title: string; Objects: CanvasObjectDto[]; PreviewUrl?: string },
      Result<string>
    >(
      '/template/CreateCanvasTemplate',
      {
        Title: title,
        Objects: objects,
        PreviewUrl: previewUrl,
      }
    );
  }

  async updateCanvasTemplate(templateId: string, title: string, objects: CanvasObjectDto[], previewUrl?: string) {
    return await this.apiClient.put<
      { TemplateId: string; Title: string; Objects: CanvasObjectDto[]; PreviewUrl?: string },
      Result<string>
    >(
      '/template/UpdateCanvasTemplate',
      {
        TemplateId: templateId,
        Title: title,
        Objects: objects,
        PreviewUrl: previewUrl,
      }
    );
  }

  async uploadTemplateFile(templateId: string, fileId: string, file: File, description?: string) {
    const formData = new FormData();
    formData.append('File', file);
    formData.append('TemplateId', templateId);
    formData.append('FileId', fileId);
    if (description) {
      formData.append('Description', description);
    }

    return await this.apiClient.postForm<Result<StoredFileDto>>(
      '/storage/UploadTemplateFile',
      formData
    );
  }
}

export type ElementDto = {
  type: string;
  value: string;
};

/** Matches API enum TemplateDefaultSlot (System.Text.Json string names). */
export type TemplateDefaultSlot = 'First' | 'Second';

export type TemplateDto = {
  id: string;
  title: string;
  tenantId: string;
  previewUrl: string;
  firstDefault: boolean;
  secondDefault: boolean;
  elements?: ElementDto[];
  objects?: CanvasObjectDto[];
};

// Base canvas object DTO
interface BaseCanvasObjectDto {
  id: string;
  position: {
    x: number;
    y: number;
  };
  size: {
    width: number;
    height: number;
  };
  color?: number;
  opacity?: number;
  zIndex?: number;
}

// Text object DTO
interface TextObjectDto extends BaseCanvasObjectDto {
  type: 'text';
  text: string;
  fontSize?: number;
  fontFamily?: string;
  fontWeight?: number;
  fontStyle?: number;
  textAlign?: number;
  isUnderlined?: boolean;
  isItalic?: boolean;
  isBold?: boolean;
  borderWidth?: number;
  borderColor?: number;
}

// Shape object DTO
interface ShapeObjectDto extends BaseCanvasObjectDto {
  type: 'rectangle' | 'circle';
  borderWidth?: number;
  borderColor?: number;
}

// Image object DTO
interface ImageObjectDto extends BaseCanvasObjectDto {
  type: 'image';
  imageUrl?: string;
  imagePath?: string;
  imageName?: string;
  borderWidth?: number;
  borderColor?: number;
}

// Connector/Arrow object DTO
interface ConnectorObjectDto extends BaseCanvasObjectDto {
  type: 'arrow';
  endPosition: {
    x: number;
    y: number;
  };
  strokeWidth?: number;
  lineStyle?: 'solid' | 'dashed' | 'dotted';
  arrowStyle?: 'straight' | 'curve' | 'elbow';
  arrowType?: 'single' | 'double';
  arrowHeadSize?: number;
  arrowHeadColor?: number;
  hasBreakpoint?: boolean;
  breakpointX?: number;
  breakpointY?: number;
  connectedFromId?: string;
  connectedToId?: string;
  connectedFromPoint?: 'top' | 'bottom' | 'left' | 'right' | 'center';
  connectedToPoint?: 'top' | 'bottom' | 'left' | 'right' | 'center';
}

export type CanvasObjectDto = TextObjectDto | ShapeObjectDto | ImageObjectDto | ConnectorObjectDto;

export type StoredFileDto = {
  id: string;
  fileName: string;
  originalFileName: string;
  fileKey: string;
  fileUrl: string;
  cdnUrl: string;
  contentType: string;
  fileSizeBytes: number;
  tenantId: string;
  uploadedByUserId: string;
  folderPath: string;
  description?: string;
  createdDateTimeUtc: string;
  updatedDateTimeUtc: string;
};
